import { useState } from 'react'
import { X, Upload, Link as LinkIcon, Loader2 } from 'lucide-react'
import { assetsApi } from '@/services/api'
import clsx from 'clsx'

interface UploadAssetModalProps {
  onClose: () => void
  onSuccess: () => void
}

type UploadMethod = 'file' | 'url'

const assetTypes = [
  { value: 'background', label: '배경' },
  { value: 'character', label: '인물' },
  { value: 'typography', label: '타이포그래피' },
  { value: 'title-typo', label: '제목 타이포' },
  { value: 'subtitle-typo', label: '부제목 타이포' },
  { value: 'author', label: '저자' },
  { value: 'publisher', label: '출판사' },
  { value: 'effect', label: '효과' },
  { value: 'layout', label: '통합' },
]

export function UploadAssetModal({ onClose, onSuccess }: UploadAssetModalProps) {
  const [uploadMethod, setUploadMethod] = useState<UploadMethod>('file')
  const [selectedFile, setSelectedFile] = useState<File | null>(null)
  const [imageUrl, setImageUrl] = useState('')
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const [type, setType] = useState('background')
  const [tags, setTags] = useState('')
  const [keywords, setKeywords] = useState('')
  const [isPublic, setIsPublic] = useState(false)
  const [isUploading, setIsUploading] = useState(false)
  const [previewUrl, setPreviewUrl] = useState<string | null>(null)

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) {
      setSelectedFile(file)
      setPreviewUrl(URL.createObjectURL(file))
      if (!name) {
        setName(file.name.replace(/\.[^/.]+$/, ''))
      }
    }
  }

  const handleUrlLoad = () => {
    if (imageUrl) {
      setPreviewUrl(imageUrl)
    }
  }

  const handleUpload = async () => {
    if (uploadMethod === 'file' && !selectedFile) {
      alert('파일을 선택해주세요')
      return
    }
    if (uploadMethod === 'url' && !imageUrl) {
      alert('URL을 입력해주세요')
      return
    }
    if (!name.trim()) {
      alert('이름을 입력해주세요')
      return
    }

    setIsUploading(true)
    try {
      let file: File

      if (uploadMethod === 'file' && selectedFile) {
        file = selectedFile
      } else {
        // URL에서 이미지 다운로드
        const response = await fetch(imageUrl)
        const blob = await response.blob()
        const filename = imageUrl.split('/').pop() || 'image.jpg'
        file = new File([blob], filename, { type: blob.type })
      }

      const tagsList = tags.split(',').map(t => t.trim()).filter(Boolean)
      const keywordsList = keywords.split(',').map(k => k.trim()).filter(Boolean)

      const result = await assetsApi.upload(file, {
        type,
        name: name.trim(),
        description: description.trim() || undefined,
        isPublic,
        tags: [...tagsList, ...keywordsList],
      })

      if (result.success) {
        onSuccess()
        onClose()
      } else {
        alert(`업로드 실패: ${result.error?.message || '알 수 없는 오류'}`)
      }
    } catch (err) {
      console.error('Upload error:', err)
      alert('업로드 중 오류가 발생했습니다')
    } finally {
      setIsUploading(false)
    }
  }

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-bg-primary rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="sticky top-0 bg-bg-primary border-b border-border-subtle p-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Upload className="w-6 h-6 text-accent-gold" />
            <h2 className="text-xl font-bold">Asset 업로드</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-bg-elevated rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {/* Upload Method */}
          <div>
            <label className="block text-sm font-medium mb-2">업로드 방식</label>
            <div className="flex gap-2">
              <button
                onClick={() => setUploadMethod('file')}
                className={clsx(
                  'flex-1 py-2 px-4 rounded-lg border transition-colors',
                  uploadMethod === 'file'
                    ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
                    : 'border-border-default hover:bg-bg-elevated'
                )}
              >
                <Upload className="w-4 h-4 inline mr-2" />
                파일 선택
              </button>
              <button
                onClick={() => setUploadMethod('url')}
                className={clsx(
                  'flex-1 py-2 px-4 rounded-lg border transition-colors',
                  uploadMethod === 'url'
                    ? 'bg-accent-gold/10 border-accent-gold text-accent-gold'
                    : 'border-border-default hover:bg-bg-elevated'
                )}
              >
                <LinkIcon className="w-4 h-4 inline mr-2" />
                URL 입력
              </button>
            </div>
          </div>

          {/* File/URL Input */}
          {uploadMethod === 'file' ? (
            <div>
              <label className="block text-sm font-medium mb-2">이미지 파일</label>
              <input
                type="file"
                accept="image/*"
                onChange={handleFileSelect}
                className="input w-full"
              />
            </div>
          ) : (
            <div>
              <label className="block text-sm font-medium mb-2">이미지 URL</label>
              <div className="flex gap-2">
                <input
                  type="url"
                  value={imageUrl}
                  onChange={(e) => setImageUrl(e.target.value)}
                  placeholder="https://example.com/image.jpg"
                  className="input flex-1"
                />
                <button
                  onClick={handleUrlLoad}
                  className="btn btn-secondary"
                >
                  미리보기
                </button>
              </div>
            </div>
          )}

          {/* Preview */}
          {previewUrl && (
            <div>
              <label className="block text-sm font-medium mb-2">미리보기</label>
              <div className="border border-border-default rounded-lg overflow-hidden bg-bg-elevated">
                <img
                  src={previewUrl}
                  alt="Preview"
                  className="w-full h-48 object-contain"
                  onError={() => {
                    setPreviewUrl(null)
                    alert('이미지를 불러올 수 없습니다')
                  }}
                />
              </div>
            </div>
          )}

          {/* Name */}
          <div>
            <label className="block text-sm font-medium mb-2">이름 *</label>
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Asset 이름"
              className="input w-full"
            />
          </div>

          {/* Type */}
          <div>
            <label className="block text-sm font-medium mb-2">카테고리 *</label>
            <select
              value={type}
              onChange={(e) => setType(e.target.value)}
              className="input w-full"
            >
              {assetTypes.map(t => (
                <option key={t.value} value={t.value}>{t.label}</option>
              ))}
            </select>
          </div>

          {/* Description */}
          <div>
            <label className="block text-sm font-medium mb-2">설명</label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Asset 설명 (선택사항)"
              rows={3}
              className="input w-full resize-none"
            />
          </div>

          {/* Tags */}
          <div>
            <label className="block text-sm font-medium mb-2">태그</label>
            <input
              type="text"
              value={tags}
              onChange={(e) => setTags(e.target.value)}
              placeholder="태그를 쉼표로 구분 (예: 판타지, 액션, 로맨스)"
              className="input w-full"
            />
            <p className="text-xs text-text-tertiary mt-1">쉼표(,)로 구분하여 입력하세요</p>
          </div>

          {/* Keywords */}
          <div>
            <label className="block text-sm font-medium mb-2">키워드</label>
            <input
              type="text"
              value={keywords}
              onChange={(e) => setKeywords(e.target.value)}
              placeholder="키워드를 쉼표로 구분 (예: 밤, 도시, 네온)"
              className="input w-full"
            />
            <p className="text-xs text-text-tertiary mt-1">검색에 사용될 키워드를 입력하세요</p>
          </div>

          {/* Public */}
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="public"
              checked={isPublic}
              onChange={(e) => setIsPublic(e.target.checked)}
              className="w-4 h-4"
            />
            <label htmlFor="public" className="text-sm">
              공개 (다른 사용자도 사용 가능)
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="sticky bottom-0 bg-bg-primary border-t border-border-subtle p-4 flex gap-2">
          <button
            onClick={onClose}
            className="btn btn-secondary flex-1"
            disabled={isUploading}
          >
            취소
          </button>
          <button
            onClick={handleUpload}
            className="btn btn-primary flex-1"
            disabled={isUploading}
          >
            {isUploading ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                업로드 중...
              </>
            ) : (
              <>
                <Upload className="w-4 h-4 mr-2" />
                업로드
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
